import { useEffect, useMemo } from "react";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";

// Direct import avoids runtime chunk loading issues in production environments.
// The component is still gated by a client-side check (typeof window) below,
// so SSR safety is preserved.
import { ClientOnly } from "./client-only";
import TimezoneFieldClient from "./timezone-field-client";

interface TimezoneFieldProps {
    value?: string;
    onChange?: (timezone: string) => void;
}

export default function TimezoneField({ value, onChange }: TimezoneFieldProps) {
    const { defaultTimezone, currentValue } = useMemo(() => {
        const defaultTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currentValue = value || defaultTimezone;
        return { defaultTimezone, currentValue };
    }, [value]);

    // AIDEV-NOTE: Ensure parent component gets the default timezone on first load
    useEffect(() => {
        if (!value && onChange) {
            onChange(defaultTimezone);
        }
    }, [value, onChange, defaultTimezone]);

    // Don't render on server
    if (typeof window === "undefined") {
        return (
            <Select disabled>
                <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a timezone" />
                </SelectTrigger>
            </Select>
        );
    }

    // Since the component relies on browser APIs (e.g., Intl for timezone names),
    // we ensure it only renders on the client. A simple disabled select acts as
    // fallback during SSR while hydration occurs.
    return (
        <ClientOnly>
            <TimezoneFieldClient value={currentValue} onChange={onChange} />
        </ClientOnly>
    );
}
